import {
	Box,
	Stack,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import BarchartInfoLogo from 'assets/BarchartInfoLogo'
import { getFatnessColorByTable } from 'components/cattles/fatnessPage/shared/fatnessScoreColor'
import { morphForWords } from 'components/shared/processes'
import { IFatnessHealthTable } from 'models/ICattlesData'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import Preloader from 'ui/Preloader'
import CustomTableRow from 'ui/table/CustomTableRow'
import TooltipHover from 'ui/TooltipHover'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { TypographySecondaryBold16 } from 'ui/typography/TypographySecondaryBold16'

const INVALID_LACTATION_TOOLTIP = (
	<span>
		Данные по дням лактации недействительны. Актуализируйте систему управления
		стадом.
		<br />
		Для корректных расчетов нормы упитанности текущий лактационный день коровы
		не должен превышать 365 дней.
	</span>
)

interface FatnessHealthTableProps {
	cowId: number
	lactationLength?: number
}

const getLactationDayAtDate = (
	currentLactationDay: number,
	recordDate: number,
) => {
	const daysSinceRecord = moment()
		.startOf('day')
		.diff(moment(recordDate).startOf('day'), 'days')

	return currentLactationDay - daysSinceRecord
}

const FatnessHealthTable = (props: FatnessHealthTableProps) => {
	const { cowId, lactationLength } = props
	const { permissions } = useAppSelector(state => state.authReducer)
	const hasFoodTableLicense = Boolean(permissions?.license_food_table)
	const [healthInfo, setHealthInfo] = useState<IFatnessHealthTable[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const tableHead = useMemo(
		() => ({
			rowKey: 'tableHistoryViewHead',
			rowBorderBottom: COLORS.secondaryFontOpacity,
			height: '40px',
			cellItems: [
				{
					width: '9%',
					children: (
						<TypographySecondary sx={{ whiteSpace: 'nowrap' }}>
							Дата
						</TypographySecondary>
					),
				},
				{
					width: hasFoodTableLicense ? '12%' : '23%',
					children: (
						<TypographySecondary sx={{ whiteSpace: 'nowrap' }}>
							Группа
						</TypographySecondary>
					),
				},
				...(hasFoodTableLicense
					? [
							{
								width: '23%',
								children: (
									<TypographySecondary sx={{ whiteSpace: 'nowrap' }}>
										Наименование рациона
									</TypographySecondary>
								),
							},
							{
								width: '15%',
								children: (
									<TypographySecondary sx={{ whiteSpace: 'nowrap' }}>
										Среднее ПСВ
									</TypographySecondary>
								),
							},
						]
					: []),
				{
					width: '15%',
					children: (
						<TypographySecondary sx={{ whiteSpace: 'nowrap' }}>
							Балл упитанности
						</TypographySecondary>
					),
				},
				{
					width: '15%',
					children: (
						<TypographySecondary sx={{ whiteSpace: 'nowrap' }}>
							Норма упитанности
						</TypographySecondary>
					),
				},
			],
		}),
		[hasFoodTableLicense],
	)

	const columnsCount = hasFoodTableLicense ? 6 : 4

	const transformForTableData = useCallback(
		(data: IFatnessHealthTable[]) => {
			return data?.map((el, index) => {
				const lactationDayAtRecord =
					typeof lactationLength === 'number'
						? getLactationDayAtDate(lactationLength, el.date)
						: null
				const isErrorLactation =
					lactationDayAtRecord == null ||
					lactationDayAtRecord <= 0 ||
					lactationDayAtRecord > 365 ||
					(typeof el.fatness_norm === 'string' &&
						el.fatness_norm.toLowerCase().includes('недействительн'))
				const fatnessTextColor = getFatnessColorByTable(
					el.fatness,
					lactationDayAtRecord,
				)

				return {
					rowKey: `keyId=${index}`,
					height: '100%',
					cellItems: [
						{
							padding: '16px 8px 0px 0px',
							children: (
								<TypographySecondary14>
									{moment(el.date).format('DD.MM.YY')}
								</TypographySecondary14>
							),
						},
						{
							padding: '16px 8px 0px 0px',
							children: (
								<TypographyPrimary key={`groupTitle-${index}`}>
									{el.group_title}
								</TypographyPrimary>
							),
						},
						...(hasFoodTableLicense
							? [
									{
										padding: '16px 8px 0px 0px',
										children: el.ration_title ? (
											<TypographyPrimary key={`rationTitle-${index}`}>
												{el.ration_title}
											</TypographyPrimary>
										) : (
											<TypographySecondary>{'-'}</TypographySecondary>
										),
									},
									{
										padding: '16px 8px 0px 0px',
										children: el.psv ? (
											<TypographyPrimary key={`psv-${index}`}>
												{el.psv}{' '}
												<span style={{ color: COLORS.secondaryFont }}>
													{'кг/гол'}
												</span>
											</TypographyPrimary>
										) : (
											<TypographySecondary>{'-'}</TypographySecondary>
										),
									},
								]
							: []),
						{
							padding: '16px 8px 0px 0px',
							children: el.fatness ? (
								<TypographyPrimary
									key={`fatness-${index}`}
									sx={{ color: fatnessTextColor }}
								>
									{el.fatness}{' '}
									<span style={{ color: COLORS.secondaryFont }}>
										{morphForWords(el.fatness, ['балл', 'балла', 'баллов'])}
									</span>
								</TypographyPrimary>
							) : (
								<TypographySecondary>{'-'}</TypographySecondary>
							),
						},
						{
							padding: '16px 8px 0px 0px',
							children: isErrorLactation ? (
								<TooltipHover
									title={INVALID_LACTATION_TOOLTIP}
									placement='top'
									oneLine={false}
									maxWidth='350px'
									disableHoverBackground
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											cursor: 'pointer',
											'&:hover svg': {
												stroke: COLORS.main,
											},
										}}
									>
										<BarchartInfoLogo
											width={'16px'}
											height={'16px'}
											stroke={COLORS.error}
										/>
									</Box>
								</TooltipHover>
							) : el.fatness_norm ? (
								<TypographySecondary14>{el.fatness_norm}</TypographySecondary14>
							) : (
								<TypographySecondary>{'-'}</TypographySecondary>
							),
						},
					],
				}
			})
		},
		[hasFoodTableLicense, lactationLength],
	)

	useEffect(() => {
		setIsLoading(true)
		CattlesApiService.getFatnessHealthTableByCowId(cowId)
			.then(res => {
				setHealthInfo(res.reverse())
			})
			.catch(err => console.error(err))
			.finally(() => setIsLoading(false))
	}, [cowId])

	const transformedData = transformForTableData(healthInfo)

	return (
		<>
			<TableContainer
				sx={{
					marginTop: '24px',
					overflowY: 'auto',
					overflowX: 'auto',
					height: '267px',
					'&::-webkit-scrollbar': {
						width: 4,
					},
					'&::-webkit-scrollbar-track': {
						backgroundColor: '#DBDBDB',
						borderRadius: 12,
					},
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: COLORS.icon,
						borderRadius: 12,
					},
				}}
			>
				<Table>
					<TableHead
						sx={{
							[`& .${tableCellClasses.root}`]: {
								backgroundColor: '#fff',
							},
						}}
					>
						<CustomTableRow
							rowKey={tableHead.rowKey}
							cellItems={tableHead.cellItems}
							rowBorderBottom={tableHead.rowBorderBottom}
							rowHeight={tableHead.height}
						/>
					</TableHead>
					<TableBody>
						{isLoading ? (
							<TableRow
								sx={{
									width: '100%',
									[`& .${tableCellClasses.root}`]: {
										borderBottom: 'none',
										height: '200px',
									},
								}}
							>
								<TableCell
									colSpan={columnsCount}
									align={'center'}
									padding={'none'}
								>
									<Preloader color={COLORS.secondaryFont} />
								</TableCell>
							</TableRow>
						) : !transformedData || transformedData.length === 0 ? (
							<TableRow
								sx={{
									width: '100%',
									[`& .${tableCellClasses.root}`]: {
										borderBottom: 'none',
										height: '200px',
									},
								}}
							>
								<TableCell
									colSpan={columnsCount}
									align={'center'}
									padding={'none'}
								>
									<Stack
										height='200px'
										direction='column'
										justifyContent='center'
										alignItems='center'
										gap='8px'
									>
										<TypographySecondaryBold16>
											Нет данных
										</TypographySecondaryBold16>
										<TypographySecondary14
											sx={{ color: '#7F7F84', fontWeight: 200 }}
										>
											Нет данных, соответствующих выбранным условиям
										</TypographySecondary14>
									</Stack>
								</TableCell>
							</TableRow>
						) : (
							transformedData.map(el => (
								<CustomTableRow
									key={el.rowKey}
									rowKey={el.rowKey}
									cellItems={el.cellItems}
									rowHeight={el.height}
								/>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{healthInfo.length > 0 && (
				<TypographySecondary sx={{ marginTop: '24px' }}>
					{'Всего: ' + healthInfo.length}
				</TypographySecondary>
			)}
		</>
	)
}

export default FatnessHealthTable
